import { PERMISSIONS } from '@nks/shared';
import { useQuery } from '@tanstack/react-query';
import { Flex, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../lib/format';
import { useHasPermission } from '../auth/session';
import { getNextService } from './api';

const { Text } = Typography;

/** Which service the motorcycle is due for next. Shown only to people who may see service reminders. */
export function NextServiceLine({ unitUid }: { unitUid: string }): ReactNode {
  const { t } = useTranslation();
  const allowed = useHasPermission(PERMISSIONS.CASE_VIEW);
  const { data, isError } = useQuery({
    queryKey: ['unit', unitUid, 'next-service'],
    queryFn: () => getNextService(unitUid),
    enabled: allowed,
  });
  if (!allowed || (data === undefined && !isError)) return null;

  const next = data?.nextService ?? null;
  const rule = next !== null ? t(`serviceDesk.reminderRule.${next.ruleCode}`, { defaultValue: next.ruleCode }) : '';
  return (
    <Flex gap={8} wrap align="baseline">
      <Text strong>{t('serviceDesk.nextService.label')}</Text>
      {isError ? (
        <Text type="secondary">{t('serviceDesk.nextService.failed')}</Text>
      ) : next === null ? (
        <Text type="secondary">{t('serviceDesk.nextService.none')}</Text>
      ) : (
        <Text>
          {next.dueMileage === null
            ? t('serviceDesk.nextService.line', { rule, date: formatDate(next.dueDate) })
            : t('serviceDesk.nextService.lineKm', {
                rule,
                date: formatDate(next.dueDate),
                km: next.dueMileage.toLocaleString('en-MY'),
              })}
        </Text>
      )}
    </Flex>
  );
}
